// Document validation utilities for CPF, CNPJ, NIF, and NIPC

/**
 * Validates Brazilian CPF
 * @param {string} cpf - The CPF to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateCPF = cpf => {
  if (!cpf) return false;

  const value = cpf.replace(/[^\d]+/g, '');
  if (value.length !== 11) return false;

  if (/^(\d)\1{10}$/.test(value)) return false;

  const calcCheckDigit = (base, factor) => {
    let sum = 0;
    for (let i = 0; i < base.length; i++) {
      sum += parseInt(base.charAt(i), 10) * (factor - i);
    }
    const result = sum % 11;
    return result < 2 ? 0 : 11 - result;
  };

  const base9 = value.substring(0, 9);
  const digit1 = calcCheckDigit(base9, 10);

  const base10 = base9 + digit1;
  const digit2 = calcCheckDigit(base10, 11);

  return value === base10 + digit2;
};

/**
 * Validates Brazilian CNPJ
 * @param {string} cnpj - The CNPJ to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateCNPJ = cnpj => {
  if (!cnpj) return false;

  const value = cnpj.toUpperCase().replace(/[^A-Z0-9]+/g, '');
  if (value.length !== 14) return false;

  const base = value.slice(0, 12);
  const verifierDigits = value.slice(12);

  if (!/^[A-Z0-9]{12}$/.test(base)) return false;
  if (!/^\d{2}$/.test(verifierDigits)) return false;

  // Keep legacy numeric repeated-sequence guard for old numeric CNPJ values.
  if (/^\d{14}$/.test(value) && /^(\d)\1{13}$/.test(value)) return false;

  const weightsDigit1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weightsDigit2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const charToValue = char => {
    if (/\d/.test(char)) return parseInt(char, 10);
    return char.charCodeAt(0) - 48;
  };

  const calcCheckDigit = (chars, weights) => {
    const sum = chars.reduce((acc, currentChar, index) => acc + charToValue(currentChar) * weights[index], 0);
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const baseChars = base.split('');
  const digit1 = calcCheckDigit(baseChars, weightsDigit1);
  const digit2 = calcCheckDigit([...baseChars, String(digit1)], weightsDigit2);

  return verifierDigits === `${digit1}${digit2}`;
};

/**
 * Validates Portuguese NIF (for both individuals and companies in Portugal)
 * @param {string} nif - The NIF to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateNIF = nif => {
  if (!nif) return false;

  const value = nif.replace(/[^\d]+/g, '');

  if (!value || value.length !== 9) {
    return false;
  }

  // Check for invalid sequences (all same digits)
  if (/^(\d)\1{8}$/.test(value)) {
    return false;
  }

  // Validate check digit
  const digits = value.split('').map(Number);
  let sum = 0;

  // Calculate weighted sum for the first 8 digits
  for (let i = 0; i < 8; i++) {
    sum += digits[i] * (9 - i);
  }

  const remainder = sum % 11;
  let checkDigit;

  if (remainder < 2) {
    checkDigit = 0;
  } else {
    checkDigit = 11 - remainder;
  }

  if (checkDigit !== digits[8]) {
    return false;
  }

  return true;
};

/**
 * Validates Portuguese NIPC (same as NIF, used for companies)
 * @param {string} nipc - The NIPC to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateNIPC = nipc => {
  // NIPC follows the same validation rules as NIF
  return validateNIF(nipc);
};

/**
 * Validates Colombian NIT (Número de Identificación Tributaria)
 * @param {string} nit - The NIT to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateNIT = nit => {
  if (!nit) return false;

  const value = nit.replace(/[^\d]+/g, '');
  if (value.length !== 10) return false;

  const base9 = value.substring(0, 9);
  const checkDigit = parseInt(value.charAt(9), 10);
  const weights = [41, 37, 29, 23, 19, 17, 13, 7, 3];

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(base9.charAt(i), 10) * weights[i];
  }

  const remainder = sum % 11;
  let expectedCheckDigit;
  if (remainder === 0 || remainder === 1) {
    expectedCheckDigit = remainder;
  } else {
    expectedCheckDigit = 11 - remainder;
  }

  return checkDigit === expectedCheckDigit;
};

/**
 * Validates Colombian Cédula de Ciudadanía (CC)
 * @param {string} cc - The CC to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateCC = cc => {
  if (!cc) return false;
  const value = cc.replace(/[^\d]+/g, '');
  return value.length >= 6 && value.length <= 10;
};

/**
 * Validates document based on type and country
 * @param {string} document - The document to validate
 * @param {string} type - The client type ('pf' for natural person, 'pj' for legal person)
 * @param {string} country - The country code ('BR', 'PT', 'CO')
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateDocument = (document, type, country) => {
  if (!document || !type || !country) return false;

  if (country === 'BR') {
    if (type === 'pf') {
      return validateCPF(document);
    } else if (type === 'pj') {
      return validateCNPJ(document);
    }
  } else if (country === 'PT') {
    // In Portugal, both PF and PJ use NIF/NIPC (same validation)
    if (type === 'pf') {
      return validateNIF(document);
    } else if (type === 'pj') {
      return validateNIPC(document);
    }
  } else if (country === 'CO') {
    if (type === 'pf') {
      return validateCC(document);
    } else if (type === 'pj') {
      return validateNIT(document);
    }
  }

  return true;
};
